import React, { useState } from 'react';
import { X, Bug, AlertTriangle, Send, CheckCircle } from 'lucide-react';

const BugReportModal = ({ isOpen, onClose, currentView, API_URL }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    severity: 'medium'
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const getBrowserInfo = () => {
    const ua = navigator.userAgent;
    let browserName = 'Unknown';
    let browserVersion = '';
    
    if (ua.indexOf('Chrome') > -1) {
      browserName = 'Chrome';
      browserVersion = ua.match(/Chrome\/(\d+)/)?.[1] || '';
    } else if (ua.indexOf('Safari') > -1) {
      browserName = 'Safari';
      browserVersion = ua.match(/Version\/(\d+)/)?.[1] || '';
    } else if (ua.indexOf('Firefox') > -1) {
      browserName = 'Firefox';
      browserVersion = ua.match(/Firefox\/(\d+)/)?.[1] || '';
    }
    
    const isMobile = /Mobile|Android|iPhone|iPad/.test(ua);
    const os = /Mac/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : 'Unknown';
    
    return `${browserName} ${browserVersion} on ${os}${isMobile ? ' (Mobile)' : ''}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/bug-reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          severity: formData.severity,
          current_page: currentView,
          browser_info: getBrowserInfo()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit bug report');
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setFormData({ title: '', description: '', severity: 'medium' });
      }, 2000);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="bug-report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3><Bug size={20} /> Report a Bug</h3>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        {success ? (
          <div className="bug-report-success">
            <CheckCircle size={48} />
            <h4>Thank you!</h4>
            <p>Your bug report has been submitted successfully.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bug-report-form">
            {error && (
              <div className="alert alert-error">
                <AlertTriangle size={16} /> {error}
              </div>
            )}

            <div className="form-group">
              <label>What went wrong?</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Brief summary of the issue"
                required
                data-testid="bug-title"
              />
            </div>

            <div className="form-group">
              <label>Please describe the issue in detail</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What were you trying to do? What happened instead? Any error messages?"
                rows={5}
                required
                data-testid="bug-description"
              />
            </div>

            <div className="form-group">
              <label>Severity</label>
              <div className="severity-options">
                <label className={`severity-option ${formData.severity === 'low' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="severity"
                    value="low"
                    checked={formData.severity === 'low'}
                    onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                  />
                  <span className="severity-badge low">Low</span>
                  <span className="severity-desc">Minor issue, workaround exists</span>
                </label>
                <label className={`severity-option ${formData.severity === 'medium' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="severity"
                    value="medium"
                    checked={formData.severity === 'medium'}
                    onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                  />
                  <span className="severity-badge medium">Medium</span>
                  <span className="severity-desc">Feature not working correctly</span>
                </label>
                <label className={`severity-option ${formData.severity === 'high' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="severity"
                    value="high"
                    checked={formData.severity === 'high'}
                    onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                  />
                  <span className="severity-badge high">High</span>
                  <span className="severity-desc">Blocking my work</span>
                </label>
                <label className={`severity-option ${formData.severity === 'critical' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="severity"
                    value="critical"
                    checked={formData.severity === 'critical'}
                    onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                  />
                  <span className="severity-badge critical">Critical</span>
                  <span className="severity-desc">Data loss or security issue</span>
                </label>
              </div>
            </div>

            <div className="form-group auto-captured">
              <span className="auto-label">Auto-captured:</span>
              <span className="auto-value">Page: {currentView} • {getBrowserInfo()}</span>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={loading} data-testid="bug-submit">
                {loading ? 'Submitting...' : <><Send size={16} /> Submit Report</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BugReportModal;
